import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../environments/environment';
import { FinanceClosureClientSample, FinanceClosureSummary, FinanceVendorSampleCost, FinanceNonSampleCost, FinanceSummaryOverview, FinanceClosureNote } from 'core';

@Injectable({
  providedIn: 'root'
})
export class FinanceService {
  baseUrl = environment.apiEndpoint + '/finance/';
  baseUrlZoho = environment.apiEndpoint + '/zoho/';

  tzoffset = new Date().getTimezoneOffset() * -1;

  constructor(private http: HttpClient) { }

  getSummaryVendorBreakdown(bidNumber): Observable<any[]> {
    return this.http.get<any[]>(this.baseUrl + `summary/${bidNumber}/vendorbreakdown`, { headers: { cache: '0' } });
  }

  getSummaryOverview(bidNumber: string) {
    return this.http.get<FinanceSummaryOverview[]>(this.baseUrl + `summary/${bidNumber}/overview`);
  }

  getAllNonSampleCosts(bidNumber: string) {
    return this.http.get<FinanceNonSampleCost[]>(this.baseUrl + `summary/${bidNumber}/nonsamplecost`);
  }

  getAllVendorSampleCosts(bidNumber: string) {
    return this.http.get<FinanceVendorSampleCost[]>(this.baseUrl + `summary/${bidNumber}/vendorsamplecost`);
  }

  getNonSampleCostById(id: string) {
    return this.http.get<FinanceNonSampleCost>(this.baseUrl + `summary/nonsamplecost/${id}`);
  }

  getVendorSampleCostById(id: string) {
    return this.http.get<FinanceVendorSampleCost>(this.baseUrl + `summary/vendorsamplecost/${id}`);
  }

  addNonSampleCost(model: FinanceNonSampleCost) {
    return this.http.post(this.baseUrl + 'summary/nonsamplecost', model);
  }

  editNonSampleCost(model: FinanceNonSampleCost): Observable<any> {
    return this.http.put(this.baseUrl + `summary/nonsamplecost/${model.id}`, model);
  }

  addNonSampleCostBulk(model: FinanceNonSampleCost[]) {
    return this.http.post(this.baseUrl + 'summary/nonsamplecost/bulk', model);
  }

  deleteNonSampleCostBulk(model: FinanceNonSampleCost[]) {
    return this.http.post(this.baseUrl + 'summary/nonsamplecost/bulkdelete', model);
  }

  updateNonSampleCostBulk(model: FinanceNonSampleCost[]) {
    return this.http.post(this.baseUrl + 'summary/nonsamplecost/bulkupdate', model);
  }


  addVendorSampleCost(model: FinanceVendorSampleCost) {
    return this.http.post(this.baseUrl + 'summary/vendorsamplecost', model);
  }

  editVendorSampleCost(model: FinanceVendorSampleCost): Observable<any> {
    return this.http.put(this.baseUrl + `summary/vendorsamplecost/${model.id}`, model);
  }

  clientFileChecksFinanceClosure(s3File: string, bidNumber: any): Observable<any> {
    const params = new HttpParams().set('s3File', s3File);
    return this.http.post<any>(`${this.baseUrl}closure/ClientFileChecksFinanceClosure/${bidNumber}`, {}, { params });
  }

  saveFinanceClosure(model: FinanceClosureSummary) {
    return this.http.post(this.baseUrl + 'closure/saveFinanceClosure', model);
  }

  invoiceFinanceClosure(model: any) {
    return this.http.post(this.baseUrl + 'closure/invoiceFinanceClosure', model);
  }

  deleteFinanceClosureData(model: FinanceClosureSummary) {
    return this.http.post(this.baseUrl + 'closure/deleteFinanceClosureData/', model);
  }

  getFinanceClosureDataByBidNumber(bidNumber: string) {
    return this.http.get<FinanceClosureSummary>(this.baseUrl + `closure/getFinanceClosureDataByBidNumber/${bidNumber}`);
  }

  getAllFinanceClosureClientSample(financeClosureSummaryId: string) {
    return this.http.get<any>(this.baseUrl + `closure/${financeClosureSummaryId}/financeClosureClientSample`);
  }

  editFinanceClosureClientSample(model: FinanceClosureClientSample) {
    return this.http.put(this.baseUrl + `closure/financeClosureClientSample/${model.id}`, model);
  }

  getFinanceClosureVendors(financeClosureSummaryId: string) {
    return this.http.get<any>(this.baseUrl + `closure/${financeClosureSummaryId}/financeClosureVendors`);
  }

  editFinanceClosureVendors(model: any) {
    return this.http.put(this.baseUrl + `closure/financeClosureVendors/${model.id}`, model);
  }

  checkProjectStatusforClosure(bidNumber: string): Observable<any> {
    return this.http.get<any>(this.baseUrl + `closure/checkProjectStatusforClosure/${bidNumber}`);
  }

  updateProjectStatusToClosed(listOfIds: Array<string>): Observable<any> {
    return this.http.post(this.baseUrl + 'closure/updateProjectStatusToClosed', listOfIds);
  }

  downloadFinanceCompletes(bidNumber, filename) {
    return this.http.get(this.baseUrl + bidNumber + '/completes?filename=' + filename + '&tzoffset=' + this.tzoffset, { responseType: 'text' });
  }

  UpdateFinanceClosureSteps(stepNumber: number, bidNumber: string): Observable<any> {
    const params = new HttpParams().set('stepNumber', stepNumber);
    return this.http.post<any>(`${this.baseUrl}closure/UpdateFinanceClosureSteps/${bidNumber}`, {}, { params });
  }
  saveNote(model: FinanceClosureNote): Observable<any> {
    return this.http.post(this.baseUrl + 'closure/saveNote', model);
  }
  getAllProjectTeam(bidNumber: string): Observable<any> {
    return this.http.get<any>(this.baseUrl + `closure/getAllProjectTeam/${bidNumber}`);
  }
  sendVendorExternalLink(closureVendor: any, bidNumber: string): Observable<any> {
    const payload = {
      bidNumber: bidNumber,
      vendor: closureVendor
    };
    return this.http.post<any>(this.baseUrl + 'closure/sendvendorexternallink', payload);
  }

  getZohoSalesOrder(bidNumber: string) {
    return this.http.get<any>(this.baseUrlZoho + `salesorder/${bidNumber}`);
  }

  getZohoProducts() {
    return this.http.get<any>(this.baseUrlZoho + `products`);
  }

  upsertZohoItems(bidNumber: string, items: any) {
    return this.http.post(this.baseUrlZoho + `salesorder/${bidNumber}/upsertitems`, items);
  }

  updateZohoSalesOrderItem(bidNumber: string, item: any, _delete: boolean = false): Observable<any> {
    return this.http.post(this.baseUrlZoho + `salesorder/${bidNumber}/updateitem?_delete=${_delete}`, item);
  }

}

